import React, { useRef, useState, useEffect } from "react";
import { Camera, RefreshCw, Check, Loader2, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

// Camera-ONLY capture (no gallery). Shows a permission explanation before requesting access.
export const CameraCapture = ({ label, purpose, facingMode = "environment", onCaptured, testId }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [stage, setStage] = useState("idle"); // idle | streaming | preview
  const [preview, setPreview] = useState(null);
  const [blob, setBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileId, setFileId] = useState(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopStream(), []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode }, audio: false,
      });
      streamRef.current = stream;
      setStage("streaming");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      toast.error("Camera permission denied or unavailable. Please allow camera access.");
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => {
      setBlob(b);
      setPreview(URL.createObjectURL(b));
      setStage("preview");
      stopStream();
    }, "image/jpeg", 0.9);
  };

  const retake = () => {
    setPreview(null);
    setBlob(null);
    setFileId(null);
    startCamera();
  };

  const confirm = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `${purpose}.jpg`);
      fd.append("purpose", purpose);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id);
      onCaptured(data.file_id, preview);
      toast.success(`${label} captured`);
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-sm">{label}</span>
        {fileId && <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><Check className="h-4 w-4" /> Saved</span>}
      </div>

      {stage === "idle" && !fileId && (
        <div className="space-y-3">
          <div className="flex gap-2 rounded-lg bg-brand-light p-3 text-xs text-brand">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <p>We use your camera only to capture this document securely. No photos are taken until you tap capture, and images are stored privately.</p>
          </div>
          <Button type="button" onClick={startCamera} className="w-full" data-testid={`${testId}-enable-camera`}>
            <Camera className="h-4 w-4 mr-2" /> Enable Camera
          </Button>
        </div>
      )}

      {stage === "streaming" && (
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-lg bg-black aspect-[3/4]">
            <video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
            <div className="pointer-events-none absolute inset-4 rounded-lg border-2 border-dashed border-white/70" />
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={capture} className="flex-1" data-testid={`${testId}-capture`}>
              <Camera className="h-4 w-4 mr-2" /> Capture
            </Button>
            <Button type="button" variant="outline" onClick={() => { stopStream(); setStage("idle"); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {stage === "preview" && (
        <div className="space-y-3">
          <div className="overflow-hidden rounded-lg aspect-[3/4] bg-black">
            <img src={preview} alt={label} className="h-full w-full object-cover" />
          </div>
          {!fileId ? (
            <div className="flex gap-2">
              <Button type="button" onClick={confirm} disabled={uploading} className="flex-1" data-testid={`${testId}-confirm`}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Use Photo
              </Button>
              <Button type="button" variant="outline" onClick={retake} data-testid={`${testId}-retake`}>
                <RefreshCw className="h-4 w-4 mr-2" /> Retake
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" onClick={retake} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" /> Retake
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
